var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useCapsuleStore, useModalStore } from '../stores/index.js';
import { ModalStep } from '../utils/steps.js';
import { WalletType, isIOS, isIOSWebview, isMobile, truncateAddress } from '@usecapsule/web-sdk';
export const defaultExternalWallet = {
    wallets: [],
    chains: [],
    chainId: undefined,
    wallet: undefined,
    qrUri: undefined,
    chainIdSwitchingTo: undefined,
    walletDisplayHelpers: {
        showExtension: false,
        showMobile: false,
        isSolanaMobileIOS: false,
        isCosmosMobileWallet: false,
    },
    username: undefined,
    avatar: undefined,
    connectExternalWallet: () => { },
    disconnectExternalWallet: () => Promise.resolve(),
    switchChain: () => Promise.resolve(),
    setChainIdSwitchingTo: () => { },
};
export const ExternalWalletContext = createContext(defaultExternalWallet);
export function ExternalWalletProvider({ children, evmContext, solanaContext, cosmosContext, walletSort, }) {
    const { wallets: evmWallets, disconnect: evmDisconnect, chains: evmChains, chainId: evmChainId, switchChain: evmSwitchChain, username: evmUsername, avatar: evmAvatar, } = useContext(evmContext);
    const { wallets: solanaWallets, disconnect: solanaDisconnect } = useContext(solanaContext);
    const { wallets: cosmosWallets, disconnect: cosmosDisconnect, chains: cosmosChains, chainId: cosmosChainId, switchChain: cosmosSwitchChain, } = useContext(cosmosContext);
    const setStep = useModalStore(state => state.setStep);
    const setStepDirection = useModalStore(state => state.setStepDirection);
    const setIsExternalWalletConnecting = useModalStore(state => state.setIsExternalWalletConnecting);
    const isExternalWalletConnecting = useModalStore(state => state.isExternalWalletConnecting);
    const setSelectedExternalWalletId = useModalStore(state => state.setSelectedExternalWalletId);
    const selectedExternalWalletId = useModalStore(state => state.selectedExternalWalletId);
    const setExternalWalletError = useModalStore(state => state.setExternalWalletError);
    const setIsUsingMobileConnector = useModalStore(state => state.setIsUsingMobileConnector);
    const isUsingMobileConnector = useModalStore(state => state.isUsingMobileConnector);
    const capsule = useCapsuleStore(state => state.capsule);
    const [qrUri, setQrUri] = useState();
    const [chainIdSwitchingTo, setChainIdSwitchingTo] = useState();
    // Filter any wallets that aren't included in the sort array, sort by the array then sort by installed extensions
    const wallets = [...evmWallets, ...solanaWallets, ...cosmosWallets]
        .filter(w => walletSort.includes(w.id.toUpperCase()))
        .sort((a, b) => walletSort.indexOf(a.id.toUpperCase()) -
        walletSort.indexOf(b.id.toUpperCase()))
        .sort((a, b) => (a.installed === b.installed ? 0 : a.installed ? -1 : 1));
    const wallet = useMemo(() => wallets.find(w => w.id === selectedExternalWalletId), [wallets, selectedExternalWalletId]);
    const updateQrUri = () => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const uri = yield ((_a = wallet === null || wallet === void 0 ? void 0 : wallet.getQrUri) === null || _a === void 0 ? void 0 : _a.call(wallet));
        setQrUri(uri);
    });
    useEffect(() => {
        if (wallet) {
            if (!qrUri) {
                updateQrUri();
            }
        }
        else if (qrUri) {
            setQrUri(undefined);
        }
    }, [wallet]);
    const chains = useMemo(() => {
        var _a, _b, _c;
        const walletType = (_c = capsule.externalWallets[(_b = (_a = capsule.currentExternalWalletAddresses) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '']) === null || _c === void 0 ? void 0 : _c.type;
        switch (walletType) {
            case WalletType.COSMOS: {
                return cosmosChains;
            }
            case WalletType.EVM: {
                return evmChains;
            }
            default: {
                return [];
            }
        }
    }, [cosmosChains, evmChains, selectedExternalWalletId]);
    const chainId = useMemo(() => {
        var _a, _b, _c;
        const walletType = (_c = capsule.externalWallets[(_b = (_a = capsule.currentExternalWalletAddresses) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '']) === null || _c === void 0 ? void 0 : _c.type;
        switch (walletType) {
            case WalletType.COSMOS: {
                return cosmosChainId;
            }
            case WalletType.EVM: {
                return evmChainId === null || evmChainId === void 0 ? void 0 : evmChainId.toString();
            }
            default: {
                return undefined;
            }
        }
    }, [cosmosChains, evmChains, selectedExternalWalletId]);
    const switchChain = useCallback((chainId) => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const walletType = (_c = capsule.externalWallets[(_b = (_a = capsule.currentExternalWalletAddresses) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '']) === null || _c === void 0 ? void 0 : _c.type;
        if (walletType) {
            let resp;
            setExternalWalletError();
            setChainIdSwitchingTo(chainId);
            switch (walletType) {
                case WalletType.COSMOS: {
                    setStep(ModalStep.CHAIN_SWITCH);
                    resp = yield cosmosSwitchChain(chainId);
                    break;
                }
                case WalletType.EVM: {
                    setStep(ModalStep.CHAIN_SWITCH);
                    resp = yield evmSwitchChain(parseInt(chainId));
                    break;
                }
                default: {
                    break;
                }
            }
            if (resp.error) {
                setExternalWalletError(resp.error);
            }
            else {
                setChainIdSwitchingTo(undefined);
                setStepDirection(-1);
                setStep(ModalStep.ACCOUNT_MAIN);
            }
        }
    }), [evmSwitchChain, cosmosSwitchChain]);
    const connectExternalWallet = useCallback((wallet, isMobileConnect) => __awaiter(this, void 0, void 0, function* () {
        if (!isExternalWalletConnecting) {
            setExternalWalletError();
            setIsExternalWalletConnecting(true);
            setIsUsingMobileConnector(isMobileConnect);
            const { address, error } = yield (isMobileConnect ? wallet.connectMobile() : wallet.connect());
            if (error) {
                setExternalWalletError([error]);
                setIsUsingMobileConnector();
            }
            else if (address) {
                setStep(ModalStep.LOGIN_DONE);
            }
            setIsExternalWalletConnecting(false);
        }
    }), [isExternalWalletConnecting]);
    const disconnectExternalWallet = () => __awaiter(this, void 0, void 0, function* () {
        yield evmDisconnect();
        yield solanaDisconnect();
        yield cosmosDisconnect();
        setSelectedExternalWalletId();
    });
    const walletDisplayHelpers = {
        // Show the extension screen if on web and the wallet is an extension and installed or the wallet isn't a mobile wallet
        // Also show the extension connection if on desktop for a solana wallet (no walletConnect)
        showExtension: !isMobile() && (((wallet === null || wallet === void 0 ? void 0 : wallet.isExtension) && (wallet === null || wallet === void 0 ? void 0 : wallet.installed)) || !(wallet === null || wallet === void 0 ? void 0 : wallet.isMobile) || (wallet === null || wallet === void 0 ? void 0 : wallet.type) === WalletType.SOLANA),
        // Show the mobile screen if on mobile and the wallet is a mobile wallet or if on desktop and the wallet isn't installed
        showMobile: (isMobile() && (wallet === null || wallet === void 0 ? void 0 : wallet.isMobile)) || (!isMobile() && !(wallet === null || wallet === void 0 ? void 0 : wallet.installed)),
        isSolanaMobileIOS: isIOS() && isMobile() && !isIOSWebview() && (wallet === null || wallet === void 0 ? void 0 : wallet.type) === WalletType.SOLANA,
        isCosmosMobileWallet: (wallet === null || wallet === void 0 ? void 0 : wallet.type) === WalletType.COSMOS && isUsingMobileConnector,
    };
    const username = useMemo(() => {
        var _a, _b;
        let username;
        const storedExternalWallet = capsule.externalWallets[(_b = (_a = capsule.currentExternalWalletAddresses) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : ''];
        if (storedExternalWallet) {
            const walletType = storedExternalWallet === null || storedExternalWallet === void 0 ? void 0 : storedExternalWallet.type;
            switch (walletType) {
                case WalletType.EVM: {
                    // If evmUsername is an EVM address, format it, else return it since it should be an ENS name
                    username = evmUsername
                        ? evmUsername.startsWith('0x')
                            ? truncateAddress(evmUsername, 'EVM')
                            : evmUsername
                        : undefined;
                    break;
                }
                default: {
                    username = storedExternalWallet.address
                        ? truncateAddress(storedExternalWallet.address, storedExternalWallet.type)
                        : undefined;
                    break;
                }
            }
        }
        return username;
    }, [evmUsername, wallet]);
    const avatar = useMemo(() => {
        var _a, _b, _c;
        const walletType = (_c = capsule.externalWallets[(_b = (_a = capsule.currentExternalWalletAddresses) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : '']) === null || _c === void 0 ? void 0 : _c.type;
        if (walletType) {
            switch (walletType) {
                case WalletType.EVM: {
                    return evmAvatar;
                }
                default: {
                    return undefined;
                }
            }
        }
    }, [evmAvatar, wallet]);
    return (_jsx(ExternalWalletContext.Provider, { value: useMemo(() => ({
            wallets,
            chains,
            chainId,
            wallet,
            qrUri,
            walletDisplayHelpers,
            chainIdSwitchingTo,
            username,
            avatar,
            connectExternalWallet,
            disconnectExternalWallet,
            switchChain,
            setChainIdSwitchingTo,
        }), [
            wallets,
            chains,
            chainId,
            wallet,
            qrUri,
            walletDisplayHelpers,
            chainIdSwitchingTo,
            username,
            avatar,
            disconnectExternalWallet,
            connectExternalWallet,
            switchChain,
            setChainIdSwitchingTo,
        ]), children: children }));
}
export const useExternalWallets = () => useContext(ExternalWalletContext);
